import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, X, Trash2, Eye, Edit, Users, Search, Check,
    ChevronDown, ChevronUp, Filter,
} from "lucide-react";

import {
    createCampagneRequest,
    deleteCampagneRequest,
    fetchCampagnesRequest,
    updateCampagneRequest,
} from "../../../../redux/actions/campagnesActions";
import { getAllEntitesRequest } from "../../../../redux/actions/entitesActions";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import { getAllJobsRequest } from "../../../../redux/actions/Jobactions";
import MultiSelectDropdown from "../../../../Ui/UiJs/MultiSelectDropdown";
import '../Campagne/Campagnerecrutement.css';

const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

/* ─── helpers ─── */
const toDateInputValue = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "" : dt.toISOString().split("T")[0];
};
const uniqNums = (arr) =>
    Array.from(new Set((arr || []).map((x) => Number(x)).filter((x) => !Number.isNaN(x))));
const splitAxes = (s) =>
    s ? String(s).split("\n").map((x) => x.trim()).filter(Boolean) : [];
const formatDateFR = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("fr-FR");
};

const EMPTY_FORM = {
    nom: "", reference: "", description: "",
    dateDebut: "", dateFin: "",
    anneeCampagne: new Date().getFullYear(),
    envoiEmails: "INSTANTANE", dateEnvoiEmails: "",
    planificationRelance: "false",
    dateRelance: "", dateRelance2: "", dateRelance3: "",
    status: true,
};

/* ─── ViewModal ─── */
function ViewModal({ open, campagne, onClose, entites }) {
    if (!open || !campagne) return null;
    const getEntName = (id) => (entites || []).find(e => +e.id === +id)?.name || `#${id}`;
    const formatAxes = (a) => a ? String(a).split("\n").filter(Boolean).join(", ") : "—";

    return (
        <div className="cr-overlay" onClick={onClose}>
            <div className="cr-modal" onClick={e => e.stopPropagation()} role="dialog">
                <div className="cr-modal__header">
                    <div className="cr-modal__title">
                        <Eye size={16} style={{ color: "#ee645f" }} />
                        Détails — {campagne.nom}
                    </div>
                    <button className="cr-icon-btn" onClick={onClose} type="button"><X size={16} /></button>
                </div>
                <div className="cr-modal__body">
                    <div className="cr-view-section">
                        <h3 className="cr-view-title">Informations générales</h3>
                        <div className="cr-view-grid">
                            <div className="cr-view-item"><span className="cr-view-label">Nom</span><span className="cr-view-value cr-strong">{campagne.nom}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Référence</span><span className="cr-view-value cr-muted">{campagne.reference || "—"}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Année</span><span className="cr-badge cr-badge--blue">{campagne.anneeCampagne}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Statut</span>{campagne.status ? <span className="cr-badge cr-badge--green">Active</span> : <span className="cr-badge cr-badge--grey">Inactive</span>}</div>
                            {campagne.description && <div className="cr-view-item cr-view-item--full"><span className="cr-view-label">Description</span><span className="cr-view-value cr-muted">{campagne.description}</span></div>}
                            {campagne.axes && <div className="cr-view-item cr-view-item--full"><span className="cr-view-label">Axes</span><span className="cr-view-value cr-muted">{formatAxes(campagne.axes)}</span></div>}
                        </div>
                    </div>
                    <div className="cr-view-section">
                        <h3 className="cr-view-title">Dates</h3>
                        <div className="cr-view-grid">
                            <div className="cr-view-item"><span className="cr-view-label">Début</span><span className="cr-view-value">{formatDateFR(campagne.dateDebut)}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Fin</span><span className="cr-view-value">{formatDateFR(campagne.dateFin)}</span></div>
                        </div>
                    </div>
                    <div className="cr-view-section">
                        <h3 className="cr-view-title">Configuration</h3>
                        <div className="cr-view-grid">
                            <div className="cr-view-item"><span className="cr-view-label">Emails</span><span className="cr-badge cr-badge--blue">{campagne.envoiEmails || "—"}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Filter mode</span><span className="cr-badge cr-badge--grey">{campagne.filterMode || "—"}</span></div>
                        </div>
                    </div>
                    {campagne.recruitmentLimits?.length > 0 && (
                        <div className="cr-view-section">
                            <h3 className="cr-view-title">Limites de recrutement</h3>
                            {campagne.recruitmentLimits.map((r, i) => (
                                <div key={i} className="cr-budget-row">
                                    <div>
                                        <div className="cr-strong" style={{ fontSize: 12 }}>{r.typeLimite}</div>
                                        <div className="cr-muted" style={{ fontSize: 11 }}>{r.entiteId ? getEntName(r.entiteId) : "Global"}</div>
                                    </div>
                                    <span className="cr-badge cr-badge--blue">Limite : {r.limite ?? 0}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {campagne.budgets?.length > 0 && (
                        <div className="cr-view-section">
                            <h3 className="cr-view-title">Budgets</h3>
                            {campagne.budgets.map((b, i) => (
                                <div key={i} className="cr-budget-row">
                                    <div>
                                        <div className="cr-strong" style={{ fontSize: 12 }}>{b.typeBudget}</div>
                                        {b.entiteId && <div className="cr-muted" style={{ fontSize: 11 }}>{getEntName(b.entiteId)}</div>}
                                    </div>
                                    <span className="cr-budget-amount">{(b.montant || 0).toLocaleString("fr-FR")} {b.currency}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {campagne.entiteIds?.length > 0 && (
                        <div className="cr-view-section">
                            <h3 className="cr-view-title">Entités ciblées</h3>
                            <div className="cr-tag-list">
                                {campagne.entiteIds.map(id => <span key={id} className="cr-badge cr-badge--grey">{getEntName(id)}</span>)}
                            </div>
                        </div>
                    )}
                </div>
                <div className="cr-modal__footer">
                    <button className="cr-btn cr-btn--ghost" type="button" onClick={onClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
}

/* ─── Inline Users Panel — identique à CampagneManagement ─── */
function InlineUsersPanel({ users, usersState, setUsersState, setSelectedUsers, entiteIds, posteIds }) {
    const [search, setSearch] = useState("");
    const [autoFilter, setAutoFilter] = useState(false);

    const AVATAR_COLORS = ["#204385", "#ee645f", "#0891b2", "#059669", "#7c3aed", "#d97706"];
    const getAvatarColor = (id) => AVATAR_COLORS[+id % AVATAR_COLORS.length];

    const getInitials = (u) => {
        const first = (u.firstName || u.prenom || "?")[0];
        const last = (u.lastName || u.nom || "")[0] || "";
        return `${first}${last}`.toUpperCase();
    };
    const getFullName = (u) =>
        `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""}`.trim() || `User #${u.id}`;

    const baseList = useMemo(() => {
        if (!autoFilter || (!entiteIds.length && !posteIds.length)) return usersState || [];
        return (usersState || []).filter(u => {
            const matchEnt = entiteIds.length === 0 || entiteIds.includes(+u.entiteId) || entiteIds.includes(+u.entityId);
            const matchPost = posteIds.length === 0 || posteIds.includes(+u.posteId) || posteIds.includes(+u.jobId);
            return matchEnt && matchPost;
        });
    }, [usersState, autoFilter, entiteIds, posteIds]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return baseList;
        return baseList.filter(u =>
            `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""} ${u.email || ""}`.toLowerCase().includes(q)
        );
    }, [baseList, search]);

    const toggleUser = (userId) => {
        const updated = usersState.map(u =>
            +u.id === +userId ? { ...u, checked: !u.checked } : u
        );
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const toggleAll = () => {
        const allChecked = filtered.every(u => u.checked);
        const filteredIds = new Set(filtered.map(u => +u.id));
        const updated = usersState.map(u =>
            filteredIds.has(+u.id) ? { ...u, checked: !allChecked } : u
        );
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const clearAll = () => {
        const updated = usersState.map(u => ({ ...u, checked: false }));
        setUsersState(updated);
        setSelectedUsers([]);
    };

    const checkedUsers = (usersState || []).filter(u => u.checked);
    const allFilteredChecked = filtered.length > 0 && filtered.every(u => u.checked);
    const hasAutoFilterOption = entiteIds.length > 0 || posteIds.length > 0;

    return (
        <div className="cr-users-panel">
            <div className="cr-users-panel__header">
                <div className="cr-users-panel__title">
                    <Users size={14} />
                    Collaborateurs
                </div>
                <div className="cr-users-header-right">
                    {hasAutoFilterOption && (
                        <button
                            type="button"
                            className={`cr-auto-filter-btn${autoFilter ? " active" : ""}`}
                            onClick={() => setAutoFilter(p => !p)}
                            title="Filtrer selon les entités et postes sélectionnés"
                        >
                            <Filter size={12} />
                            Filtrer par entités/postes
                        </button>
                    )}
                    <div className="cr-users-search">
                        <Search size={12} className="cr-users-search-icon" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className="cr-users-search-input"
                        />
                    </div>
                </div>
            </div>

            {checkedUsers.length > 0 && (
                <div className="cr-users-summary">
                    <div className="cr-avatar-stack">
                        {checkedUsers.slice(0, 5).map(u => (
                            <div
                                key={u.id}
                                className="cr-avatar-stack__item"
                                style={{ background: getAvatarColor(u.id) }}
                                title={getFullName(u)}
                            >
                                {getInitials(u)}
                            </div>
                        ))}
                        {checkedUsers.length > 5 && (
                            <div className="cr-avatar-stack__more">+{checkedUsers.length - 5}</div>
                        )}
                    </div>
                    <span className="cr-users-summary__label">
                        <strong>{checkedUsers.length}</strong> collaborateur{checkedUsers.length > 1 ? "s" : ""} sélectionné{checkedUsers.length > 1 ? "s" : ""}
                    </span>
                    <button type="button" className="cr-users-summary__clear" onClick={clearAll}>
                        <X size={11} /> Tout désélectionner
                    </button>
                </div>
            )}

            {(usersState || []).length === 0 ? (
                <div className="cr-users-empty">
                    <Users size={28} style={{ color: "#d1d5db", marginBottom: 8 }} />
                    <div>Aucun utilisateur disponible</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                        Vérifiez que les utilisateurs sont chargés depuis le store Redux
                    </div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="cr-users-empty">
                    Aucun résultat{search ? ` pour « ${search} »` : " avec ce filtre"}
                </div>
            ) : (
                <>
                    <div className="cr-users-select-all" onClick={toggleAll}>
                        <div className={`cr-user-checkbox${allFilteredChecked ? " checked" : ""}`}>
                            {allFilteredChecked && <Check size={10} />}
                        </div>
                        <span className="cr-users-select-text">
                            {allFilteredChecked ? "Tout désélectionner" : "Tout sélectionner"} ({filtered.length})
                        </span>
                    </div>
                    <div className="cr-users-list">
                        {filtered.map(u => (
                            <div
                                key={u.id}
                                className={`cr-user-row${u.checked ? " checked" : ""}`}
                                onClick={() => toggleUser(u.id)}
                            >
                                <div className={`cr-user-checkbox${u.checked ? " checked" : ""}`}>
                                    {u.checked && <Check size={10} />}
                                </div>
                                <div
                                    className="cr-user-avatar"
                                    style={u.checked ? { background: getAvatarColor(u.id), color: "#fff" } : {}}
                                >
                                    {getInitials(u)}
                                </div>
                                <div className="cr-user-info">
                                    <div className="cr-user-name">{getFullName(u)}</div>
                                    {u.email && <div className="cr-user-email">{u.email}</div>}
                                </div>
                                {u.poste && (
                                    <span className="cr-badge cr-badge--grey" style={{ fontSize: 10, marginLeft: "auto" }}>
                                        {u.poste}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

/* ─── FormModal ─── */
function FormModal({ open, campagneId, campagnes, onClose, onSave, isLoading, entites, jobs, users }) {
    const campagne = useMemo(() =>
            campagneId ? (campagnes || []).find(c => +c.id === +campagneId) || null : null,
        [campagneId, campagnes]);

    const [form, setForm] = useState(EMPTY_FORM);
    const [filterMode, setFilterMode] = useState("IN");
    const [managerLevelFilter, setManagerLevelFilter] = useState("");
    const [entiteIds, setEntiteIds] = useState([]);
    const [posteIds, setPosteIds] = useState([]);
    const [usersState, setUsersState] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialUserIds, setInitialUserIds] = useState([]);
    const [recruitmentLimits, setRecruitmentLimits] = useState([{ typeLimite: "Global", entiteId: "", limite: "" }]);
    const [axeToAdd, setAxeToAdd] = useState("");
    const [axesArray, setAxesArray] = useState([]);
    const [currency, setCurrency] = useState("MAD");
    const [budgetGlobal, setBudgetGlobal] = useState("");
    const [budgetsByEntite, setBudgetsByEntite] = useState([]);
    const [errors, setErrors] = useState({});
    const [showUsersPanel, setShowUsersPanel] = useState(false);
    const [activeSection, setActiveSection] = useState("identification");

    // --- New Activation Fields ---
    const [activationPurpose, setActivationPurpose] = useState("");
    const [activationUserIds, setActivationUserIds] = useState([]);
    const [activationDateDebut, setActivationDateDebut] = useState("");
    const [activationDateFin, setActivationDateFin] = useState("");
    const [isDefault, setIsDefault] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (!campagne) {
            setForm(EMPTY_FORM);
            setFilterMode("IN"); setManagerLevelFilter(""); setEntiteIds([]); setPosteIds([]);
            setRecruitmentLimits([{ typeLimite: "Global", entiteId: "", limite: "" }]);
            setCurrency("MAD");
            setBudgetGlobal("");
            setBudgetsByEntite([]);
            setUsersState([]); setSelectedUsers([]); setInitialUserIds([]);
            setAxeToAdd(""); setAxesArray([]); setErrors({}); setActiveSection("identification");
            setActivationPurpose(""); setActivationUserIds([]);
            setActivationDateDebut(""); setActivationDateFin(""); setIsDefault(false);
            return;
        }
        setForm({
            nom: campagne.nom || "", reference: campagne.reference || "",
            description: campagne.description || "",
            dateDebut: toDateInputValue(campagne.dateDebut),
            dateFin: toDateInputValue(campagne.dateFin),
            anneeCampagne: campagne.anneeCampagne || new Date().getFullYear(),
            envoiEmails: campagne.envoiEmails || "INSTANTANE",
            dateEnvoiEmails: toDateInputValue(campagne.dateEnvoiEmails),
            planificationRelance: String(campagne.planificationRelance ?? "false"),
            dateRelance: toDateInputValue(campagne.dateRelance),
            dateRelance2: toDateInputValue(campagne.dateRelance2),
            dateRelance3: toDateInputValue(campagne.dateRelance3),
            status: Boolean(campagne.status ?? true),
        });
        setFilterMode(campagne.filterMode || "IN");
        setManagerLevelFilter(campagne.managerLevelFilter || "");
        setEntiteIds(uniqNums(campagne.entiteIds || []));
        setPosteIds(uniqNums(campagne.posteIds || []));
        setInitialUserIds(uniqNums(campagne.userIds || []));
        setAxesArray(splitAxes(campagne.axes));
        setRecruitmentLimits(
            campagne.recruitmentLimits?.length
                ? campagne.recruitmentLimits.map(r => ({ typeLimite: r.typeLimite || "Global", entiteId: r.entiteId ?? "", limite: r.limite ?? "" }))
                : [{ typeLimite: "Global", entiteId: "", limite: "" }]
        );

        const bList = campagne.budgets || [];
        const globalBudget = bList.find(b => String(b.typeBudget || "").toLowerCase().includes("global"));
        const entiteBudgets = bList.filter(b => String(b.typeBudget || "").toLowerCase().includes("entit"));

        setCurrency(globalBudget?.currency || entiteBudgets[0]?.currency || "MAD");
        setBudgetGlobal(globalBudget?.montant != null ? String(globalBudget.montant) : "");
        setBudgetsByEntite(
            entiteBudgets.map(b => ({
                entiteId: b.entiteId ?? "",
                currency: b.currency || globalBudget?.currency || "MAD",
                montant: b.montant != null ? String(b.montant) : "",
            }))
        );

        setErrors({}); setActiveSection("identification");

        setActivationPurpose(campagne.activationPurpose || "");
        setActivationUserIds(uniqNums(campagne.activationUserIds || []));
        setActivationDateDebut(toDateInputValue(campagne.activationDateDebut));
        setActivationDateFin(toDateInputValue(campagne.activationDateFin));
        setIsDefault(Boolean(campagne.isDefault || false));
    }, [open, campagne]);

    useEffect(() => {
        const prevMap = new Map((usersState || []).map(u => [+u.id, !!u.checked]));
        const initSet = new Set((initialUserIds || []).map(x => +x));
        const next = (users || []).map(u => ({
            ...u,
            checked: prevMap.has(+u.id) ? prevMap.get(+u.id) : initSet.has(+u.id)
        }));
        setUsersState(next);
        setSelectedUsers(next.filter(u => u.checked));
    }, [users, initialUserIds]);

    const filteredUsersForActivation = useMemo(() => {
        if (!entiteIds.length && !posteIds.length) return users || [];
        return (users || []).filter(u => {
            const matchEnt = entiteIds.length === 0 || entiteIds.includes(+u.entiteId) || entiteIds.includes(+u.entityId);
            const matchPost = posteIds.length === 0 || posteIds.includes(+u.posteId) || posteIds.includes(+u.jobId);
            return matchEnt && matchPost;
        });
    }, [users, entiteIds, posteIds]);

    const totalBudgetsByEntite = useMemo(() => {
        return (budgetsByEntite || []).reduce((sum, row) => {
            const montant = Number(row.montant || 0);
            return sum + (Number.isNaN(montant) ? 0 : montant);
        }, 0);
    }, [budgetsByEntite]);

    const globalBudgetNumber = Number(budgetGlobal || 0);
    const hasGlobalBudget = !Number.isNaN(globalBudgetNumber) && globalBudgetNumber > 0;

    const remainingBudget = hasGlobalBudget
        ? Math.max(0, globalBudgetNumber - totalBudgetsByEntite)
        : null;

    const addBudgetEntiteRow = () => {
        if (hasGlobalBudget && totalBudgetsByEntite >= globalBudgetNumber) {
            showSwal({
                icon: "warning",
                text: "Le budget global est déjà entièrement réparti sur les entités.",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        setBudgetsByEntite((prev) => [
            ...prev,
            { entiteId: "", currency, montant: "" }
        ]);
    };

    const removeBudgetEntiteRow = (idx) => {
        setBudgetsByEntite((prev) => prev.filter((_, i) => i !== idx));
    };

    const updateBudgetEntiteRow = (idx, field, value) => {
        const nextRows = budgetsByEntite.map((row, i) =>
            i === idx ? { ...row, [field]: value } : row
        );

        if (field === "entiteId") {
            const currentEntiteId = Number(value);
            const duplicated = nextRows.some(
                (row, i) => i !== idx && Number(row.entiteId) === currentEntiteId && currentEntiteId > 0
            );

            if (duplicated) {
                showSwal({
                    icon: "warning",
                    text: "Cette entité a déjà un budget.",
                    timer: 1800,
                    showConfirmButton: false,
                });
                return;
            }
        }

        if (field === "montant" && hasGlobalBudget) {
            const nextTotal = nextRows.reduce((sum, row) => {
                const montant = Number(row.montant || 0);
                return sum + (Number.isNaN(montant) ? 0 : montant);
            }, 0);

            if (nextTotal > globalBudgetNumber) {
                showSwal({
                    icon: "warning",
                    text: "La somme des budgets par entité ne doit pas dépasser le budget global.",
                    timer: 2200,
                    showConfirmButton: false,
                });
                return;
            }
        }

        setBudgetsByEntite(nextRows);
    };

    const validate = () => {
        const e = {};

        if (!form.nom.trim()) e.nom = "Obligatoire";
        if (!form.description.trim()) e.description = "Obligatoire";

        if (
            !recruitmentLimits.length ||
            recruitmentLimits.some(r =>
                !r.typeLimite ||
                Number.isNaN(+r.limite) ||
                +r.limite < 0 ||
                (String(r.typeLimite).toLowerCase().includes("entit") && !r.entiteId)
            )
        ) {
            e.recruitmentLimits = "Limites invalides";
        }

        const cleanBudgetsByEntite = budgetsByEntite.filter(r => r.entiteId || r.montant);

        if (!hasGlobalBudget && cleanBudgetsByEntite.length === 0) {
            e.budgetGlobal = "Veuillez renseigner au moins un budget global ou un budget par entité";
        }

        if (budgetGlobal && (+budgetGlobal <= 0 || Number.isNaN(+budgetGlobal))) {
            e.budgetGlobal = "Budget global invalide";
        }

        if (
            cleanBudgetsByEntite.some(r =>
                !r.entiteId || Number(r.montant) <= 0 || Number.isNaN(Number(r.montant))
            )
        ) {
            e.budgetsByEntite = "Budget par entité invalide";
        }

        const entiteIdsUsed = cleanBudgetsByEntite
            .map(r => Number(r.entiteId))
            .filter(id => !Number.isNaN(id) && id > 0);

        const duplicatedEntite = entiteIdsUsed.some(
            (id, index) => entiteIdsUsed.indexOf(id) !== index
        );

        if (duplicatedEntite) {
            e.budgetsByEntite = "Une même entité ne peut pas avoir plusieurs budgets";
        }

        const totalEntites = cleanBudgetsByEntite.reduce((sum, row) => sum + Number(row.montant || 0), 0);

        if (hasGlobalBudget && totalEntites > globalBudgetNumber) {
            e.budgetsByEntite = "La somme des budgets par entité dépasse le budget global";
        }

        if (form.envoiEmails === "DIFFERE" && !form.dateEnvoiEmails) {
            e.dateEnvoiEmails = "Date obligatoire";
        }

        if (form.planificationRelance === "true" && !form.dateRelance) {
            e.dateRelance = "Date relance 1 obligatoire";
        }

        setErrors(e);
        return !Object.keys(e).length;
    };

    const buildPayload = () => {
        const budgets = [
            ...(hasGlobalBudget
                ? [{
                    montant: +budgetGlobal,
                    currency,
                    typeBudget: "Global",
                }]
                : []),

            ...budgetsByEntite
                .filter(r => r.entiteId && r.montant)
                .map(r => ({
                    entiteId: +r.entiteId,
                    montant: +r.montant,
                    currency: r.currency || currency,
                    typeBudget: "Par entité",
                })),
        ];

        return {
            nom: form.nom,
            reference: form.reference,
            description: form.description,
            dateDebut: form.dateDebut || null,
            dateFin: form.dateFin || null,
            anneeCampagne: +form.anneeCampagne,
            typeCampagne: "RECRUTEMENT",
            filterMode,
            managerLevelFilter: managerLevelFilter || null,
            entiteIds: uniqNums(entiteIds),
            posteIds: uniqNums(posteIds),
            userIds: uniqNums(selectedUsers.map(u => u.id)),
            catalogueIds: [],
            nombreMaxFormations: 1,
            recruitmentLimits: recruitmentLimits.map(r => ({
                typeLimite: r.typeLimite,
                limite: +r.limite,
                entiteId: String(r.typeLimite).toLowerCase().includes("entit") ? +r.entiteId : null
            })),
            envoiEmails: form.envoiEmails,
            dateEnvoiEmails: form.envoiEmails === "DIFFERE" ? form.dateEnvoiEmails || null : null,
            planificationRelance: form.planificationRelance === "true",
            dateRelance: form.planificationRelance === "true" ? form.dateRelance || null : null,
            dateRelance2: form.planificationRelance === "true" ? form.dateRelance2 || null : null,
            dateRelance3: form.planificationRelance === "true" ? form.dateRelance3 || null : null,
            axes: axesArray.join("\n"),
            status: Boolean(form.status),
            budgets,
            activationPurpose,
            activationUserIds: uniqNums(activationUserIds),
            activationDateDebut: activationDateDebut || null,
            activationDateFin: activationDateFin || null,
            isDefault: Boolean(isDefault),
        };
    };

    const handleSubmit = () => { if (!validate()) return; onSave(campagneId, buildPayload()); };

    const addAxe = () => {
        const v = axeToAdd.trim();
        if (!v || axesArray.includes(v)) { setAxeToAdd(""); return; }
        setAxesArray(p => [...p, v]);
        setAxeToAdd("");
    };

    const sf = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

    const sections = [
        { id: "identification", label: "Identification" },
        { id: "limites", label: "Limites" },
        { id: "population", label: "Population" },
        { id: "activation", label: "Activation" },
        { id: "budget", label: "Budget" },
        { id: "relances", label: "Relances" },
        { id: "axes", label: "Axes" },
        { id: "autres", label: "Autres" },
    ];

    const sectionHasError = (id) => {
        if (id === "identification") return !!(errors.nom || errors.description);
        if (id === "limites") return !!errors.recruitmentLimits;
        if (id === "budget") return !!(errors.budgetGlobal || errors.budgetsByEntite);
        if (id === "relances") return !!errors.dateRelance;
        if (id === "autres") return !!errors.dateEnvoiEmails;
        return false;
    };

    if (!open) return null;

    return (
        <div className="cr-overlay" style={{ zIndex: 9998 }}>
            <div className="cr-modal cr-modal--wide" role="dialog">
                <div className="cr-modal__header">
                    <div className="cr-modal__title">
                        <Users size={16} style={{ color: "#ee645f" }} />
                        {campagneId ? "Modifier la campagne de recrutement" : "Créer une campagne de recrutement"}
                    </div>
                    <button className="cr-icon-btn" onClick={onClose} type="button"><X size={16} /></button>
                </div>

                <div className="cr-section-nav">
                    {sections.map(s => (
                        <button key={s.id} type="button"
                                className={`cr-section-tab${activeSection === s.id ? " active" : ""}${sectionHasError(s.id) ? " has-error" : ""}`}
                                onClick={() => setActiveSection(s.id)}>
                            {s.label}
                            {sectionHasError(s.id) && <span className="cr-tab-dot" />}
                        </button>
                    ))}
                </div>

                <div className="cr-modal__body cr-form-body">

                    {activeSection === "identification" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Identification</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <label>Nom *</label>
                                    <input value={form.nom} onChange={sf("nom")} placeholder="Ex: Campagne Recrutement 2025" />
                                    {errors.nom && <div className="cr-error">{errors.nom}</div>}
                                </div>
                                <div className="cr-field">
                                    <label>Référence</label>
                                    <input value={form.reference} onChange={sf("reference")} placeholder="Ex: CPG-RH-2025-001" />
                                </div>
                                <div className="cr-field cr-field--full">
                                    <label>Description *</label>
                                    <textarea value={form.description} onChange={sf("description")} rows={3} placeholder="Description de la campagne de recrutement..." />
                                    {errors.description && <div className="cr-error">{errors.description}</div>}
                                </div>
                                <div className="cr-field">
                                    <label>Date début</label>
                                    <input type="date" value={form.dateDebut} onChange={sf("dateDebut")} />
                                </div>
                                <div className="cr-field">
                                    <label>Date fin</label>
                                    <input type="date" value={form.dateFin} onChange={sf("dateFin")} />
                                </div>
                                <div className="cr-field">
                                    <label>Année campagne</label>
                                    <input type="number" value={form.anneeCampagne} onChange={sf("anneeCampagne")} />
                                </div>
                                <div className="cr-field">
                                    <label>Campagne par défaut</label>
                                    <div style={{ display: "flex", gap: 20, marginTop: 10 }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="radio"
                                                name="isDefaultId"
                                                checked={isDefault === true}
                                                onChange={() => setIsDefault(true)}
                                            />
                                            <span>Oui</span>
                                        </label>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="radio"
                                                name="isDefaultId"
                                                checked={isDefault === false}
                                                onChange={() => setIsDefault(false)}
                                            />
                                            <span>Non</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeSection === "limites" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Limites de recrutement</h3>
                            {errors.recruitmentLimits && <div className="cr-error" style={{ marginBottom: 12 }}>{errors.recruitmentLimits}</div>}
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {recruitmentLimits.map((r, idx) => (
                                    <div key={idx} style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr auto",
                                        gap: "10px 12px",
                                        alignItems: "flex-end",
                                        padding: "12px 14px",
                                        background: "#f8f9fa",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 8,
                                    }}>
                                        <div className="cr-field">
                                            <label>Type limite</label>
                                            <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                                                {["Global", "Par entité"].map(opt => (
                                                    <label key={opt} className="cr-pill-radio">
                                                        <input
                                                            type="radio"
                                                            name={`typeLimite-${idx}`}
                                                            value={opt}
                                                            checked={r.typeLimite === opt}
                                                            onChange={() => setRecruitmentLimits(p => p.map((x, i) =>
                                                                i === idx ? { ...x, typeLimite: opt, entiteId: opt === "Par entité" ? x.entiteId : "" } : x
                                                            ))}
                                                        />
                                                        <span className={`cr-pill${r.typeLimite === opt ? " active" : ""}`}>
                    {opt}
                </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="cr-field">
                                            <label>Entité</label>
                                            <select value={r.entiteId} disabled={r.typeLimite !== "Par entité"} onChange={e => setRecruitmentLimits(p => p.map((x, i) => i === idx ? { ...x, entiteId: e.target.value } : x))}>
                                                <option value="">—</option>
                                                {(entites || []).map(en => <option key={en.id} value={en.id}>{en.nom || en.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="cr-field">
                                            <label>Limite *</label>
                                            <input type="number" min={0} value={r.limite} onChange={e => setRecruitmentLimits(p => p.map((x, i) => i === idx ? { ...x, limite: e.target.value } : x))} />
                                        </div>
                                        <div style={{ display: "flex", gap: 8, alignItems: "center", paddingBottom: 1 }}>
                                            <button type="button" className="cr-btn cr-btn--primary" onClick={() => setRecruitmentLimits(p => [...p, { typeLimite: "Global", entiteId: "", limite: "" }])}>+ Ajouter</button>
                                            {recruitmentLimits.length > 1 && (
                                                <button type="button" className="cr-btn cr-btn--danger" onClick={() => setRecruitmentLimits(p => p.filter((_, i) => i !== idx))}>Retirer</button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeSection === "population" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Population cible</h3>

                            <div className="cr-filters-inline">
                                <div className="cr-filter-group">
                                    <span className="cr-filter-group__label">Mode de filtrage</span>
                                    <div className="cr-filter-group__options">
                                        {[
                                            { value: "IN", label: "IN" },
                                            { value: "NOT_IN", label: "NOT IN" },
                                        ].map(opt => (
                                            <label key={opt.value} className="cr-pill-radio">
                                                <input
                                                    type="radio"
                                                    name="filterMode"
                                                    value={opt.value}
                                                    checked={filterMode === opt.value}
                                                    onChange={() => setFilterMode(opt.value)}
                                                />
                                                <span className={`cr-pill${filterMode === opt.value ? " active" : ""}`}>
                                                    {opt.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="cr-form-grid" style={{ marginTop: 14 }}>
                                <div className="cr-field">
                                    <MultiSelectDropdown
                                        label="Entités"
                                        items={entites || []}
                                        value={entiteIds}
                                        onChange={setEntiteIds}
                                        placeholder={entiteIds.length > 0 ? `${entiteIds.length} sélectionnée(s)` : "Sélectionner..."}
                                        getId={x => +x.id}
                                        getLabel={x => x.nom || x.name || `#${x.id}`}
                                    />
                                    {entiteIds.length > 0 && (
                                        <div className="cr-selected-tags">
                                            {entiteIds.map(id => {
                                                const ent = (entites || []).find(e => +e.id === id);
                                                return (
                                                    <span key={id} className="cr-selected-tag">
                                                        {ent ? (ent.nom || ent.name) : `#${id}`}
                                                        <button type="button" onClick={() => setEntiteIds(p => p.filter(x => x !== id))}>
                                                            <X size={9} />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="cr-field">
                                    <MultiSelectDropdown
                                        label="Postes"
                                        items={jobs || []}
                                        value={posteIds}
                                        onChange={setPosteIds}
                                        placeholder={posteIds.length > 0 ? `${posteIds.length} sélectionné(s)` : "Sélectionner..."}
                                        getId={x => +x.id}
                                        getLabel={x => x.nom || x.name || `#${x.id}`}
                                    />
                                    {posteIds.length > 0 && (
                                        <div className="cr-selected-tags">
                                            {posteIds.map(id => {
                                                const job = (jobs || []).find(j => +j.id === id);
                                                return (
                                                    <span key={id} className="cr-selected-tag">
                                                        {job ? (job.nom || job.name) : `#${id}`}
                                                        <button type="button" onClick={() => setPosteIds(p => p.filter(x => x !== id))}>
                                                            <X size={9} />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="cr-collab-toggle" style={{ marginTop: 16 }}>
                                <div className="cr-collab-toggle__info">
                                    <div className="cr-strong" style={{ fontSize: 12 }}>Collaborateurs</div>
                                    <div className="cr-hint">
                                        {selectedUsers.length > 0
                                            ? `${selectedUsers.length} collaborateur${selectedUsers.length > 1 ? "s" : ""} sélectionné${selectedUsers.length > 1 ? "s" : ""}`
                                            : "Aucun collaborateur sélectionné"}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={`cr-btn${showUsersPanel ? " cr-btn--ghost" : " cr-btn--primary"}`}
                                    onClick={() => setShowUsersPanel(p => !p)}
                                >
                                    <Users size={14} />
                                    {showUsersPanel ? "Masquer" : "Afficher les collaborateurs"}
                                    {showUsersPanel ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                </button>
                            </div>

                            {showUsersPanel && (
                                <InlineUsersPanel
                                    users={users || []}
                                    usersState={usersState}
                                    setUsersState={setUsersState}
                                    setSelectedUsers={setSelectedUsers}
                                    entiteIds={entiteIds}
                                    posteIds={posteIds}
                                />
                            )}
                        </div>
                    )}

                    {activeSection === "activation" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Conditions d'activation</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <MultiSelectDropdown
                                        label="Pour qui (Population active)"
                                        items={filteredUsersForActivation || []}
                                        value={activationUserIds}
                                        onChange={setActivationUserIds}
                                        placeholder={activationUserIds.length > 0 ? `${activationUserIds.length} sélectionné(s)` : "Sélectionner..."}
                                        getId={x => +x.id}
                                        getLabel={x => `${x.firstName || x.prenom || ""} ${x.lastName || x.nom || ""}`.trim() || `#${x.id}`}
                                    />
                                    {activationUserIds.length > 0 && (
                                        <div className="cr-selected-tags">
                                            {activationUserIds.map(id => {
                                                const u = (users || []).find(x => +x.id === id);
                                                return (
                                                    <span key={id} className="cr-selected-tag">
                                                    {u ? `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""}`.trim() : `#${id}`}
                                                        <button type="button" onClick={() => setActivationUserIds(p => p.filter(x => x !== id))}>
                                                        <X size={9} />
                                                    </button>
                                                </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="cr-field">
                                    <label>Pour quoi (Objectif)</label>
                                    <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
                                        {[
                                            { value: "VALIDER", label: "Valider" },
                                            { value: "TRAITER", label: "Traiter" },
                                        ].map(opt => (
                                            <label key={opt.value} className="cr-pill-radio">
                                                <input
                                                    type="radio"
                                                    name="activationPurpose"
                                                    value={opt.value}
                                                    checked={activationPurpose === opt.value}
                                                    onChange={() => setActivationPurpose(opt.value)}
                                                />
                                                <span className={`cr-pill${activationPurpose === opt.value ? " active" : ""}`}>
                                                {opt.label}
                                            </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="cr-field">
                                    <label>Date début activation</label>
                                    <input type="date" value={activationDateDebut} onChange={e => setActivationDateDebut(e.target.value)} />
                                </div>
                                <div className="cr-field">
                                    <label>Date fin activation</label>
                                    <input type="date" value={activationDateFin} onChange={e => setActivationDateFin(e.target.value)} />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeSection === "budget" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Budget</h3>

                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <label>Devise</label>
                                    <select value={currency} onChange={e => setCurrency(e.target.value)}>
                                        <option value="MAD">MAD</option>
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr auto",
                                    gap: "10px 12px",
                                    alignItems: "flex-end",
                                    padding: "12px 14px",
                                    background: "#f8f9fa",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                }}>
                                    <div className="cr-field">
                                        <label>Type budget</label>
                                        <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                                            <label className="cr-pill-radio">
                                                <input type="radio" checked readOnly />
                                                <span className="cr-pill active">Global</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="cr-field">
                                        <label>Montant global</label>
                                        <input
                                            type="number"
                                            min={0}
                                            value={budgetGlobal}
                                            onChange={(e) => {
                                                const nextValue = e.target.value;

                                                if (nextValue === "") {
                                                    setBudgetGlobal("");
                                                    return;
                                                }

                                                const nextGlobal = Number(nextValue);
                                                if (Number.isNaN(nextGlobal) || nextGlobal < 0) return;

                                                if (totalBudgetsByEntite > nextGlobal) {
                                                    showSwal({
                                                        icon: "warning",
                                                        text: "Le budget global ne peut pas être inférieur au total des budgets par entité.",
                                                        timer: 2200,
                                                        showConfirmButton: false,
                                                    });
                                                    return;
                                                }

                                                setBudgetGlobal(nextValue);
                                            }}
                                            placeholder="Ex: 500000"
                                        />
                                        {errors.budgetGlobal && <div className="cr-error">{errors.budgetGlobal}</div>}
                                    </div>

                                    <div style={{ display: "flex", gap: 8, alignItems: "center", paddingBottom: 1 }}>
                                        <button
                                            type="button"
                                            className="cr-btn cr-btn--primary"
                                            onClick={addBudgetEntiteRow}
                                        >
                                            + Ajouter budget entité
                                        </button>
                                    </div>
                                </div>

                                {errors.budgetsByEntite && (
                                    <div className="cr-error" style={{ marginTop: -2 }}>
                                        {errors.budgetsByEntite}
                                    </div>
                                )}

                                {hasGlobalBudget && (
                                    <div style={{
                                        padding: "12px 14px",
                                        background: "#f8f9fa",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 8,
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr",
                                        gap: "10px 12px",
                                    }}>
                                        <div>
                                            <div className="cr-strong" style={{ fontSize: 12 }}>Budget global</div>
                                            <div className="cr-muted" style={{ fontSize: 12 }}>
                                                {globalBudgetNumber.toLocaleString("fr-FR")} {currency}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="cr-strong" style={{ fontSize: 12 }}>Total entités</div>
                                            <div className="cr-muted" style={{ fontSize: 12 }}>
                                                {totalBudgetsByEntite.toLocaleString("fr-FR")} {currency}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="cr-strong" style={{ fontSize: 12 }}>Reste à répartir</div>
                                            <div className="cr-muted" style={{ fontSize: 12 }}>
                                                {(remainingBudget || 0).toLocaleString("fr-FR")} {currency}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {budgetsByEntite.map((row, idx) => (
                                    <div key={idx} style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr auto",
                                        gap: "10px 12px",
                                        alignItems: "flex-end",
                                        padding: "12px 14px",
                                        background: "#f8f9fa",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 8,
                                    }}>
                                        <div className="cr-field">
                                            <label>Type budget</label>
                                            <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                                                <label className="cr-pill-radio">
                                                    <input type="radio" checked readOnly />
                                                    <span className="cr-pill active">Par entité</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="cr-field">
                                            <label>Entité *</label>
                                            <select
                                                value={row.entiteId}
                                                onChange={e => updateBudgetEntiteRow(idx, "entiteId", e.target.value)}
                                            >
                                                <option value="">—</option>
                                                {(entites || []).map(en => <option key={en.id} value={en.id}>{en.nom || en.name}</option>)}
                                            </select>
                                        </div>

                                        <div className="cr-field">
                                            <label>Montant *</label>
                                            <input
                                                type="number"
                                                min={0}
                                                value={row.montant}
                                                onChange={e => updateBudgetEntiteRow(idx, "montant", e.target.value)}
                                            />
                                        </div>

                                        <div style={{ display: "flex", gap: 8, alignItems: "center", paddingBottom: 1 }}>
                                            <button
                                                type="button"
                                                className="cr-btn cr-btn--danger"
                                                onClick={() => removeBudgetEntiteRow(idx)}
                                            >
                                                Retirer
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {budgetsByEntite.length === 0 && (
                                    <div className="cr-hint">Aucun budget par entité ajouté.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeSection === "relances" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Relances planifiées</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <label>Relances planifiées</label>

                                    <div style={{ display: "flex", gap: 20, marginTop: 10 }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="radio"
                                                name="planificationRelance"
                                                value="true"
                                                checked={form.planificationRelance === "true"}
                                                onChange={sf("planificationRelance")}
                                            />
                                            <span>Oui</span>
                                        </label>

                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="radio"
                                                name="planificationRelance"
                                                value="false"
                                                checked={form.planificationRelance === "false"}
                                                onChange={sf("planificationRelance")}
                                            />
                                            <span>Non</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {form.planificationRelance === "true" && (
                                <div className="cr-form-grid" style={{ marginTop: 12 }}>
                                    <div className="cr-field">
                                        <label>Date relance 1 *</label>
                                        <input type="date" value={form.dateRelance} onChange={sf("dateRelance")} />
                                        {errors.dateRelance && <div className="cr-error">{errors.dateRelance}</div>}
                                    </div>
                                    <div className="cr-field">
                                        <label>Date relance 2</label>
                                        <input type="date" value={form.dateRelance2} onChange={sf("dateRelance2")} />
                                    </div>
                                    <div className="cr-field">
                                        <label>Date relance 3</label>
                                        <input type="date" value={form.dateRelance3} onChange={sf("dateRelance3")} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === "axes" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Axes de recrutement</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field cr-field--full">
                                    <label>Ajouter un axe</label>
                                    <div style={{ display: "flex", gap: 10 }}>
                                        <input
                                            value={axeToAdd}
                                            onChange={e => setAxeToAdd(e.target.value)}
                                            onKeyDown={e => e.key === "Enter" && addAxe()}
                                            placeholder="Ex: Talent, Mobilité interne..."
                                            style={{ flex: 1 }}
                                        />
                                        <button type="button" className="cr-btn cr-btn--primary" onClick={addAxe}>+ Ajouter</button>
                                    </div>
                                </div>
                            </div>
                            <div className="cr-axes-list">
                                {axesArray.length === 0
                                    ? <div className="cr-hint">Aucun axe ajouté.</div>
                                    : axesArray.map((axe, idx) => (
                                        <div key={idx} className="cr-axe-row">
                                            <span className="cr-badge cr-badge--blue">{axe}</span>
                                            <button type="button" className="cr-icon-btn cr-icon-btn--danger" onClick={() => setAxesArray(p => p.filter((_, i) => i !== idx))}>
                                                <X size={13} />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {activeSection === "autres" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Paramètres email & statut</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <label>Envoi emails</label>
                                    <select value={form.envoiEmails} onChange={sf("envoiEmails")}>
                                        <option value="INSTANTANE">Instantané</option>
                                        <option value="DIFFERE">Différé</option>
                                        <option value="SANS_EMAIL">Sans email</option>
                                    </select>
                                </div>
                                {form.envoiEmails === "DIFFERE" && (
                                    <div className="cr-field">
                                        <label>Date envoi emails *</label>
                                        <input type="date" value={form.dateEnvoiEmails} onChange={sf("dateEnvoiEmails")} />
                                        {errors.dateEnvoiEmails && <div className="cr-error">{errors.dateEnvoiEmails}</div>}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="cr-modal__footer">
                    <button className="cr-btn cr-btn--ghost" type="button" onClick={onClose} disabled={isLoading}>Annuler</button>
                    <button className="cr-btn cr-btn--primary" type="button" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? "Enregistrement..." : campagneId ? "Enregistrer" : "Créer la campagne"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Main ─── */
export default function CampagneRecrutement() {
    const dispatch = useDispatch();

    const campagnesState = useSelector(s => s.campagnes || {});
    const campagnes = campagnesState.list || [];
    const campagnesLoading = !!campagnesState.loading;

    const jobs = useSelector(s => s.jobs?.jobs || []);
    const entites = useSelector(s => s.entite?.entites || []);
    const users = useSelector(s => s.user?.users || s.users?.users || []);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [selectedCampagne, setSelectedCampagne] = useState(null);
    const [tableQ, setTableQ] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllEntitesRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    const openAdd = () => { setEditId(null); setFormModalOpen(true); };
    const openEdit = (c) => { setEditId(c.id); setFormModalOpen(true); };
    const openView = (c) => { setSelectedCampagne(c); setViewModalOpen(true); };

    const handleSave = useCallback((cid, payload) => {
        if (cid) dispatch(updateCampagneRequest(cid, payload));
        else dispatch(createCampagneRequest(payload));
        showSwal({ icon: "success", text: cid ? "Campagne modifiée" : "Campagne créée", timer: 1200, showConfirmButton: false });
        setFormModalOpen(false);
    }, [dispatch]);

    const remove = async (c) => {
        const r = await showSwal({
            icon: "warning", title: "Supprimer ?", text: c.nom,
            showCancelButton: true, confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385", confirmButtonText: "Supprimer", cancelButtonText: "Annuler"
        });
        if (r.isConfirmed) dispatch(deleteCampagneRequest(c.id));
    };

    const filteredCampagnes = useMemo(() => {
        const q = tableQ.trim().toLowerCase();
        const byType = (campagnes || []).filter(c => String(c.typeCampagne || "").toUpperCase() === "RECRUTEMENT");
        const byStatus = filterStatus === "all" ? byType :
            filterStatus === "active" ? byType.filter(c => !!c.status) :
                byType.filter(c => !c.status);
        if (!q) return byStatus;
        return byStatus.filter(c => `${c.nom || ""} ${c.reference || ""} ${c.anneeCampagne || ""}`.toLowerCase().includes(q));
    }, [campagnes, tableQ, filterStatus]);

    return (
        <div className="cr-page">
            <div className="cr-header">
                <div>
                    <h1>Campagnes de recrutement</h1>
                    <p>Gérez et planifiez vos campagnes de recrutement</p>
                </div>
                <div className="cr-header-actions">
                    <button className="cr-btn cr-btn--primary" type="button" onClick={openAdd}>
                        <Plus size={15} /> Ajouter une campagne
                    </button>
                </div>
            </div>

            <div className="cr-toolbar">
                <div className="cr-search-wrap">
                    <Search size={14} className="cr-search-icon" />
                    <input
                        type="text"
                        className="cr-search"
                        value={tableQ}
                        onChange={e => setTableQ(e.target.value)}
                        placeholder="Nom, référence, année..."
                    />
                </div>
                <div className="cr-filter-tabs">
                    {[
                        { key: "all", label: "Toutes" },
                        { key: "active", label: "Actives" },
                        { key: "inactive", label: "Inactives" },
                    ].map(f => (
                        <button key={f.key} type="button"
                                className={`cr-filter-tab${filterStatus === f.key ? " active" : ""}`}
                                onClick={() => setFilterStatus(f.key)}>
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="cr-meta">
                    {campagnesLoading ? "..." : `${filteredCampagnes.length} campagne${filteredCampagnes.length !== 1 ? "s" : ""}`}
                </div>
            </div>

            <div className="cr-wrap">
                <div className="cr-card">
                    <div className="cr-body">
                        <div className="cr-table-wrap">
                            <table className="cr-table">
                                <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Référence</th>
                                    <th>Année</th>
                                    <th>Statut</th>
                                    <th>Emails</th>
                                    <th>Relances</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {campagnesLoading ? (
                                    <tr>
                                        <td colSpan={7}>
                                            <div className="cr-loading">
                                                <div className="cr-spinner" />
                                                <span>Chargement...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredCampagnes.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="cr-empty-cell">Aucune campagne Recrutement trouvée</td>
                                    </tr>
                                ) : filteredCampagnes.map(c => (
                                    <tr key={c.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <Users size={13} style={{ color: "#ee645f", flexShrink: 0 }} />
                                                <div>
                                                    <div className="cr-strong">{c.nom}</div>
                                                    {c.description && (
                                                        <div className="cr-muted" style={{ fontSize: 11, marginTop: 2 }}>
                                                            {c.description.length > 55 ? c.description.substring(0, 55) + "…" : c.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="cr-muted">{c.reference || "—"}</td>
                                        <td><span className="cr-badge cr-badge--blue">{c.anneeCampagne}</span></td>
                                        <td>
                                            {c.status
                                                ? <span className="cr-badge cr-badge--green">Active</span>
                                                : <span className="cr-badge cr-badge--grey">Inactive</span>
                                            }
                                        </td>
                                        <td><span className="cr-badge cr-badge--grey">{c.envoiEmails || "—"}</span></td>
                                        <td>
                                            {c.planificationRelance
                                                ? <span className="cr-badge cr-badge--orange">Planifiée</span>
                                                : <span className="cr-badge cr-badge--grey">Non</span>
                                            }
                                        </td>
                                        <td className="is-right">
                                            <div className="cr-row-actions">
                                                <button className="cr-icon-btn" type="button" title="Voir les détails" onClick={() => openView(c)}>
                                                    <Eye size={15} />
                                                </button>
                                                <button className="cr-icon-btn" type="button" title="Modifier" onClick={() => openEdit(c)}>
                                                    <Edit size={15} />
                                                </button>
                                                <button className="cr-icon-btn cr-icon-btn--danger" type="button" title="Supprimer" onClick={() => remove(c)}>
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <ViewModal
                open={viewModalOpen}
                campagne={selectedCampagne}
                onClose={() => { setViewModalOpen(false); setSelectedCampagne(null); }}
                entites={entites}
            />
            <FormModal
                open={formModalOpen}
                campagneId={editId}
                campagnes={campagnes}
                onClose={() => setFormModalOpen(false)}
                onSave={handleSave}
                isLoading={campagnesLoading}
                entites={entites}
                jobs={jobs}
                users={users}
            />
        </div>
    );
}